import { ref, computed, onMounted } from 'vue'
import { whenever } from '@vueuse/core'
import { api } from '../services'
import { useUserStore } from '../stores/userStore'
import { useWhitelabelUserIntegration } from './useWhitelabelUserIntegration'
import type { Reward } from '../types'

export const useRewardsData = () => {
  // Reactive state
  const rewards = ref<Reward[]>([])
  const quests = ref<Reward[]>([])
  const isLoading = ref(true)
  const hasLoadedInitialData = ref(false)
  const selectedReward = ref<Reward | null>(null)
  const selectedQuest = ref<Reward | null>(null)

  // Store references
  const userStore = useUserStore()
  const whitelabelUserIntegration = useWhitelabelUserIntegration()

  // Computed properties
  const userPoints = computed(() => userStore.userPoints)
  const isAdvancedSegment = computed(() => whitelabelUserIntegration.isAdvancedUserWithFeatures.value)
  const currentFilter = ref<'all' | 'available' | 'redeemed'>('all')

  // Filter rewards based on current filter and affordability
  const filteredRewards = computed(() => {
    switch (currentFilter.value) {
      case 'available':
        return rewards.value.filter(reward => userPoints.value >= reward.points_required)
      case 'redeemed':
        return rewards.value.filter(reward => reward.tags?.redeemed === 'true')
      default:
        return rewards.value
    }
  })

  // Group rewards by category
  const groupedRewards = computed(() => {
    const filtered = filteredRewards.value
    const grouped: Record<string, Reward[]> = {}
    
    filtered.forEach(reward => {
      const category = reward.category || 'Other'
      if (!grouped[category]) {
        grouped[category] = []
      }
      grouped[category].push(reward)
    })
    
    return grouped
  })

  // Load rewards data
  const loadRewards = async () => {
    try {
      const data = await api.rewards.getRewards()
      rewards.value = data || []
    } catch (error) {
      console.error('Error loading rewards data:', error)
      rewards.value = []
    }
  }

  // Load quests data (only for advanced users)
  const loadQuests = async () => {
    if (!isAdvancedSegment.value) {
      quests.value = []
      return
    }
    
    try {
      const data = await api.rewards.getQuests()
      quests.value = data || []
    } catch (error) {
      console.error('Error loading quests:', error)
      quests.value = []
    }
  }

  // Main load function
  const loadData = async () => {
    isLoading.value = true
    try {
      await Promise.all([
        loadRewards(),
        loadQuests()
      ])
      hasLoadedInitialData.value = true
    } finally {
      isLoading.value = false
    }
  }

  // Refresh data function for pull-to-refresh
  const refreshData = async (event?: CustomEvent): Promise<(() => void) | undefined> => {
    await loadData()
    
    // Return completion callback for component layer to handle
    if (event?.target) {
      return () => {
        const refresher = event.target as HTMLIonRefresherElement
        if (refresher?.complete) {
          refresher.complete()
        }
      }
    }
  }

  // Check if reward is affordable
  const isAffordable = (reward: Reward): boolean => {
    return userPoints.value >= reward.points_required
  }

  // Handle reward click
  const handleRewardClick = (reward: Reward) => {
    selectedReward.value = reward
  }

  // Handle quest click
  const handleQuestClick = (quest: Reward) => {
    selectedQuest.value = quest
  }

  // Get quest progress
  const getQuestProgress = (quest: Reward): number => {
    const progress = Number(quest.tags?.progress || 0)
    const target = Number(quest.tags?.target || 100)
    return Math.min(progress / target, 1)
  }

  // Get quest progress text
  const getQuestProgressText = (quest: Reward): string => {
    const progress = quest.tags?.progress || '0'
    const target = quest.tags?.target || '100'
    return `${progress}/${target}`
  }

  // Redeem reward
  const redeemReward = async (reward: Reward): Promise<boolean> => {
    if (!isAffordable(reward)) {
      return false
    }

    try {
      const success = await userStore.spendPoints(reward.points_required)
      
      if (success) {
        // Mark reward as redeemed (store as string to match tags type)
        const updatedReward = { ...reward, tags: { ...reward.tags, redeemed: 'true' } }
        await api.rewards.updateReward(updatedReward)
        
        // Update local rewards array
        const index = rewards.value.findIndex(r => r.id === reward.id)
        if (index !== -1) {
          rewards.value[index] = updatedReward
        }
      }
      
      return success
    } catch (error) {
      console.error('Error redeeming reward:', error)
      return false
    }
  }

  // Setup reactive user change handling with VueUse whenever
  whenever(() => userStore.user, async (newUser) => {
    if (newUser && hasLoadedInitialData.value) {
      // Reload data when user changes
      await loadData()
    }
  })

  // Load initial data when mounted
  onMounted(async () => {
    if (!hasLoadedInitialData.value) {
      await loadData()
    }
  })

  return {
    // State
    rewards,
    quests,
    isLoading,
    isAdvancedSegment,
    currentFilter,
    selectedReward,
    selectedQuest,
    userPoints,
    groupedRewards,
    
    // Methods
    loadData,
    refreshData,
    isAffordable,
    handleRewardClick,
    handleQuestClick,
    getQuestProgress,
    getQuestProgressText,
    redeemReward
  }
} 